import { CalendarDays, User } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useBusinessDailySummary, useDailySummary } from '../hooks/useDailySummary';
import { useEmailSummary } from '../hooks/useEmailSummary';
import { UserSelector, ALL_USERS, useUserOptions } from '../components/domain/daily/UserSelector';
import { MobileFilterSheet, type FilterDescriptor } from '../components/ui/MobileFilterSheet';
import { CalendarEventsSection } from '../components/domain/daily/CalendarEventsSection';
import { RecommendationsSection } from '../components/domain/daily/RecommendationsSection';
import { TasksSection } from '../components/domain/daily/TasksSection';
import { AllTasksByProjectSection } from '../components/domain/daily/AllTasksByProjectSection';
import { BusinessSummarySection } from '../components/domain/daily/BusinessSummarySection';
import { EmailSummarySection } from '../components/domain/daily/EmailSummarySection';
import { MeetingsSection } from '../components/domain/daily/MeetingsSection';
import { MyDailyStatusSection } from '../components/domain/daily/MyDailyStatusSection';
import { TeamDailyReportSection } from '../components/domain/daily/TeamDailyReportSection';
import { useTeamDailyReport } from '../hooks/useDailyStatus';
import { FadeIn, SkeletonList } from '../components/ui/Skeleton';
import { DateField } from '../components/ui/DateField';
import { useI18n } from '../contexts/I18nContext';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../store/useAuthStore';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Daily Summary page (Module 4; CLE-58 business overview).
 *
 * Defaults to the business-wide "All Users" view: every active team member with
 * their tasks grouped on a due-date timeline, and no calendar. Selecting
 * an individual user from the dropdown switches to that person's summary —
 * including their Google Calendar (only the configured owner, e.g. Andrei Pata,
 * has events). Selection + date persist in the URL.
 */
export function DailySummaryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useI18n();
  const { matrix, can } = usePermissions();
  const canExportPdf = can('dailySummary', 'canExportPdf');
  const canSendEmail = can('dailySummary', 'canSendEmail');
  const currentUserId = useAuthStore((s) => s.user?.id);

  // Callers whose dailySummary scope is 'self' (e.g. the Developer role) may
  // only ever see their own summary — the "All Users" overview and other users
  // are off-limits and enforced by the backend with 403 (CLE-440). Admins and
  // 'all'-scoped roles keep the business-wide default.
  const selfOnly = matrix ? matrix.dailySummary.scope !== 'all' : false;

  // Default to the business-wide overview (All Users) on first load (CLE-58),
  // unless the caller is scoped to self — then pin the selection to them.
  const requested = searchParams.get('userId');
  const selection = selfOnly ? (currentUserId ?? requested ?? '') : (requested ?? ALL_USERS);
  const isAllUsers = selection === ALL_USERS;
  const date = searchParams.get('date') ?? todayIso();

  const business = useBusinessDailySummary(date, isAllUsers);
  const individual = useDailySummary(isAllUsers ? undefined : selection, date);
  const email = useEmailSummary(isAllUsers ? undefined : selection, date);
  // The check-in roll-up feeds the per-member badges on the "All users" cards
  // and the team report section under them (one request for both).
  const teamReport = useTeamDailyReport(date, isAllUsers && !selfOnly);
  const isSelf = !isAllUsers && (selfOnly || selection === currentUserId);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next);
  };

  const isLoading = isAllUsers ? business.isLoading : individual.isLoading;
  const isError = isAllUsers ? business.isError : individual.isError;

  // Mobile: the same two controls, in the bottom sheet the tasks pages use.
  // "Today" and "All users" are the defaults, so neither counts as applied.
  const userOptions = useUserOptions();
  const mobileFilters: FilterDescriptor[] = [
    ...(!selfOnly
      ? [
          {
            kind: 'select',
            id: 'user',
            label: t('common.user'),
            icon: <User size={16} />,
            value: selection,
            onChange: (v) => updateParam('userId', v || ALL_USERS),
            options: userOptions.options,
            loading: userOptions.loading,
            defaultValue: ALL_USERS,
          } satisfies FilterDescriptor,
        ]
      : []),
    {
      kind: 'date',
      id: 'date',
      label: t('daily.date'),
      icon: <CalendarDays size={16} />,
      value: date,
      onChange: (v) => updateParam('date', v || todayIso()),
      defaultValue: todayIso(),
    },
  ];
  const clearAllFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('userId');
    next.delete('date');
    setSearchParams(next);
  };

  return (
    <div className="w-full p-4 sm:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('daily.title')}</h1>
        {/* Phones: one "Filtre" trigger opening the bottom sheet, like the
            tasks pages. Desktop keeps the inline controls below. */}
        <MobileFilterSheet
          className="sm:hidden"
          filters={mobileFilters}
          onClearAll={clearAllFilters}
        />
        {/* `items-end` and not `items-center`: the controls now carry a label
            above them, so aligning their bottoms lines the triggers up. */}
        <div className="hidden gap-3 sm:flex sm:flex-row sm:flex-wrap sm:items-end">
          {/* Self-scoped callers have no user choice to make; the picker is
              hidden and they always see their own summary (CLE-440). */}
          {!selfOnly && (
            <UserSelector selectedUserId={selection} onChange={(id) => updateParam('userId', id)} />
          )}
          {/* Label above the field, like every filter on the tasks page. Inline
              it left the trigger too narrow on a phone to fit its own clear and
              calendar icons. */}
          <div className="w-full sm:w-44">
            <label className="ui-field-label" htmlFor="daily-date">
              {t('daily.date')}
            </label>
            <DateField
              id="daily-date"
              className="w-full"
              value={date}
              onChange={(v) => updateParam('date', v)}
            />
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <SkeletonList rows={5} />
          <SkeletonList rows={5} badge />
        </div>
      )}
      {isError && <p className="text-sm text-red-600">{t('daily.loadError')}</p>}

      {/* Business-wide overview (default): per-user timeline, no calendar. */}
      {isAllUsers && business.data && (
        <FadeIn>
          <p className="mb-4 text-sm text-gray-600">
            {t('daily.allUsers')} · {business.data.date}
          </p>
          <BusinessSummarySection summary={business.data} team={teamReport.data} />
          <div className="mt-6">
            <TeamDailyReportSection
              date={date}
              canExportPdf={canExportPdf}
              canSendEmail={canSendEmail}
            />
          </div>
        </FadeIn>
      )}

      {/* Individual user: timeline buckets + calendar + all tasks by project. */}
      {!isAllUsers && individual.data && (
        <FadeIn>
          <p className="mb-4 text-sm text-gray-600">
            {individual.data.user.name} · {individual.data.date}
          </p>
          {/* Check-in board: live controls on your own day, read-only for a
              manager looking at someone else's (or at a past day). */}
          <div className="mb-6">
            <MyDailyStatusSection
              userId={individual.data.user.id}
              date={date}
              isSelf={isSelf}
              canExportPdf={canExportPdf}
              canSendEmail={canSendEmail}
            />
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <CalendarEventsSection events={individual.data.calendar.events} />
            <TasksSection
              grouped={individual.data.tasks.grouped}
              currentUserId={individual.data.user.id}
            />
          </div>
          {/* AI Insights / Recommendations from the calendar (CLE-269).
              Renders nothing when the day has no recommendations; the real
              backend omits the field entirely (Test Mode only). */}
          {(individual.data.recommendations ?? []).length > 0 && (
            <div className="mt-6">
              <RecommendationsSection recommendations={individual.data.recommendations ?? []} />
            </div>
          )}
          <div className="mt-6">
            <AllTasksByProjectSection
              byProject={individual.data.tasks.byProject}
              statuses={individual.data.tasks.statuses}
              total={individual.data.tasks.total}
            />
          </div>
          {/* Meeting Intelligence & Prep (CLE-234): upcoming "Meet [Nume]"
              meetings with on-demand AI research + prep modal. */}
          <div className="mt-6">
            <MeetingsSection userId={individual.data.user.id} date={date} />
          </div>
          {/* Daily Email Summary (CLE-233): read-only Gmail + AI processing.
              Only the connected owner mailbox returns emails. */}
          {email.data && (
            <div className="mt-6">
              <EmailSummarySection summary={email.data} userId={individual.data.user.id} />
            </div>
          )}
        </FadeIn>
      )}
    </div>
  );
}
